import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCreateBackdatedHoursRequest } from '../../hooks/useBackdatedHoursRequests';
import { parseDecimalInput } from '../../utils/parseDecimalInput';
import { toErrorMessage } from '../../utils/toErrorMessage';
import type { Project } from '../../services/api/timeEntry';
import { DateField } from '../ui/DateField';

/**
 * Phase 113 (MKS-208, SC3) — filing a backdated-hours request from the point of
 * refusal.
 *
 * The time-entry dialog opens this pre-filled with whatever the user was
 * already trying to save, so the only thing they have to add is why the hours
 * are late. D-03: approving this is the only way those hours reach the locked
 * month.
 */
export interface BackdatedHoursPrefill {
  projectId: number;
  date: string;
  hours?: number;
  task?: string | null;
  description?: string | null;
  /**
   * MKS-271 — opened from the locked-month notice above the grid rather than
   * from a refused save, so there is no day yet: the user picks one inside the
   * dialog, bounded to `month` (`YYYY-MM`) because only that month is closed.
   * `projectIds` are the locked projects to choose between; with one, the
   * dialog just names it.
   */
  pickDate?: { month: string; projectIds: number[] };
}

/** First and last day of `YYYY-MM`, as the date input's bounds. */
function monthBounds(month: string): { min: string; max: string } {
  const [year, monthNum] = month.split('-').map(Number);
  const lastDay = new Date(year as number, monthNum as number, 0).getDate();
  return { min: `${month}-01`, max: `${month}-${String(lastDay).padStart(2, '0')}` };
}

interface BackdatedHoursRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  prefill: BackdatedHoursPrefill | null;
  projects: Project[];
  onSubmitted?: () => void;
}

export function BackdatedHoursRequestModal({
  isOpen,
  onClose,
  prefill,
  projects,
  onSubmitted,
}: BackdatedHoursRequestModalProps) {
  const createMutation = useCreateBackdatedHoursRequest();
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  // MKS-271 — only used on the notice path, where the day (and sometimes the
  // project) is still open. On the refused-save path these mirror the prefill.
  const [date, setDate] = useState('');
  const [projectId, setProjectId] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setHours(prefill?.hours !== undefined ? String(prefill.hours) : '');
    setReason('');
    setError('');
    setDate(prefill?.date ?? '');
    setProjectId(prefill ? String(prefill.projectId) : '');
  }, [isOpen, prefill]);

  if (!isOpen || !prefill) return null;

  const pickDate = prefill.pickDate;
  const selectedProjectId = pickDate ? Number(projectId) : prefill.projectId;
  const selectedDate = pickDate ? date : prefill.date;
  const project = projects.find((p) => p.id === selectedProjectId);
  const month = pickDate ? pickDate.month : prefill.date.slice(0, 7);
  const lockedProjects = pickDate
    ? projects.filter((p) => pickDate.projectIds.includes(p.id))
    : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedDate || selectedDate.slice(0, 7) !== month) {
      setError(`Pick the day in ${month} the hours belong to`);
      return;
    }

    const hoursNum = parseDecimalInput(hours);
    if (hoursNum === null || hoursNum <= 0 || hoursNum > 24) {
      setError('Hours must be between 0 and 24');
      return;
    }
    if (!reason.trim()) {
      setError('Say why these hours are late — the approver needs a reason');
      return;
    }

    try {
      await createMutation.mutateAsync({
        projectId: selectedProjectId,
        date: selectedDate,
        hours: hoursNum,
        task: prefill.task?.trim() || undefined,
        description: prefill.description?.trim() || undefined,
        reason: reason.trim(),
      });
      onSubmitted?.();
      onClose();
    } catch (err) {
      setError(toErrorMessage(err, 'Failed to file the request'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

        <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Request backdated hours</h3>
            <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium">{month}</span> is locked on{' '}
              <span className="font-medium">{project?.fullName || project?.name || 'this project'}</span>
              , so the hours cannot go in directly. The project manager or an admin reviews this
              request; approving it creates the entry for you.
            </p>

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
            )}

            {pickDate ? (
              <>
                {lockedProjects.length > 1 && (
                  <div>
                    <label
                      htmlFor="backdated-project"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Project
                    </label>
                    <select
                      id="backdated-project"
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {lockedProjects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.fullName || p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label
                    htmlFor="backdated-date"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Date <span className="text-red-500">*</span>
                  </label>
                  <DateField
                    id="backdated-date"
                    value={date}
                    onChange={setDate}
                    min={monthBounds(month).min}
                    max={monthBounds(month).max}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            ) : (
              <dl className="text-sm bg-gray-50 rounded-lg p-3 space-y-1">
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Date</dt>
                  <dd className="text-gray-900">{prefill.date}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Task</dt>
                  <dd className="text-gray-900">{prefill.task || '—'}</dd>
                </div>
                {prefill.description && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Description</dt>
                    <dd className="text-gray-900 text-right">{prefill.description}</dd>
                  </div>
                )}
              </dl>
            )}

            <div>
              <label
                htmlFor="backdated-hours"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Hours
              </label>
              <input
                id="backdated-hours"
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                min="0"
                max="24"
                step="any"
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label
                htmlFor="backdated-reason"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Why are these hours late? <span className="text-red-500">*</span>
              </label>
              <textarea
                id="backdated-reason"
                value={reason}
                maxLength={1000}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. Emergency hotfix on the 29th, logged after the month closed"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BackdatedHoursRequestModal;
